import { computed, Injectable, isDevMode, signal, WritableSignal } from '@angular/core';
import { createPublicClient, encodeAbiParameters, Hex, http, keccak256, PublicClient } from 'viem';
import { environment } from '../../environments/environment';
import { Address } from '../data/address-config';
import { createAppChain } from '../data/chain';
import { FACET_UNLOCKED_MASK, levelForExp } from '../data/facets';
import { EngineABI } from '../generated/abi/engine';
import { GachaTeamRegistryABI } from '../generated/abi/gachaTeamRegistry';
import { MulticallABI } from '../generated/abi/multicall';
import { SignedMatchmakerABI } from '../generated/abi/signed-matchmaker';

// Low 128 bits of PlayerProfile.playerData hold the points balance.
const POINTS_MASK_128 = (1n << 128n) - 1n;

const ROLL_COST_LOAD_WAIT_MS = 3000;

export interface MatchmakerStatus {
  isCPUApproved: boolean;
  isSignedMatchmakerApproved: boolean;
}

// Parallel arrays returned by GachaTeamRegistry.getPlayerTeams(player):
// slots[i] is the slot id at display position i; monIds[i] is its mon set.
export type PlayerTeamsResult = readonly [readonly bigint[], readonly (readonly bigint[])[]];

// Parallel arrays from GachaTeamRegistry.getOwnedMonDetails(player): index i describes monIds[i] —
// its exp, level, uint12 facet-unlock bitmap, equipped facet (0 = none), and 8-bit move-selection
// lane set (already 0→default-collapsed). One read replaces the getOwned → per-mon-detail fan-out.
type OwnedMonDetails = readonly [
  readonly bigint[], // monIds
  readonly bigint[], // exp
  readonly bigint[], // levels
  readonly number[], // facetUnlocked (uint16)
  readonly number[], // facetEquipped (uint8)
  readonly number[], // moveSelections (uint8)
];

// Mirrors Engine.computeBattleKey's pairHash branch: addresses are ordered
// numerically (lo, hi) before hashing so the result is symmetric in (a, b).
function computePairHash(a: Hex, b: Hex): Hex {
  const [lo, hi] = BigInt(a) <= BigInt(b) ? [a, b] : [b, a];
  return keccak256(encodeAbiParameters([{ type: 'address' }, { type: 'address' }], [lo, hi]));
}

/**
 * Centralized service for all onchain state.
 * Single source of truth - other services derive their state from here.
 */
@Injectable({
  providedIn: 'root',
})
export class OnchainStateService {
  private publicClient: PublicClient;

  private resolveFirstLoad!: () => void;
  private readonly firstLoad = new Promise<void>((resolve) => { this.resolveFirstLoad = resolve; });

  private _isLoading = signal<boolean>(false);
  private _isLoaded = signal<boolean>(false);
  private _activeAddress = signal<Hex | null>(null);
  private _ethBalance = signal<bigint>(0n);
  private _ownedMonIndices = signal<readonly bigint[] | undefined>(undefined);

  // Chain owns liveness and ordering via teamOrderPacked; we mirror the live set + mons here.
  private _orderedSlots = signal<readonly number[]>([]);
  private _monIndicesByTeam = signal<ReadonlyMap<number, readonly bigint[]>>(new Map());
  private _activeTeamIndex = signal<number>(0);
  // Maps are keyed by stringified monId; bigint isn't a valid Map key in JS.
  private _levelExpByMonId = signal<ReadonlyMap<string, { level: number; exp: number }>>(new Map());
  // `unlocked` is a uint12 bitmap (bits 0..11 → facet ids 1..12);
  // `equipped` is 0 for none, 1..12 for the assigned facet.
  private _facetByMonId = signal<ReadonlyMap<string, { unlocked: number; equipped: number }>>(new Map());
  // Battle-move loadout: an 8-bit lane set (bit i = catalog lane i selected). The chain view
  // already collapses the 0-bitmap default to lanes 0..3, so a present value is always the real set.
  private _moveSelByMonId = signal<ReadonlyMap<string, number>>(new Map());
  private _isCPUApproved = signal<boolean>(false);
  private _isSignedMatchmakerApproved = signal<boolean>(false);

  private _pointsBalance = signal<bigint>(0n);

  // Full packed per-player profile word (PlayerProfile.sol). Points are its low 128 bits;
  // QuestService decodes the last-quest-day field above bit 128.
  private _playerData = signal<bigint>(0n);

  private _rollCost = signal<bigint>(0n);

  private _openBattleOfferNonce = signal<bigint>(0n);
  private _pairHashNonces = signal<Map<Hex, bigint>>(new Map());

  readonly isLoading = this._isLoading.asReadonly();
  readonly isLoaded = this._isLoaded.asReadonly();
  readonly activeAddress = this._activeAddress.asReadonly();
  readonly ethBalance = this._ethBalance.asReadonly();
  readonly ownedMonIndices = this._ownedMonIndices.asReadonly();
  readonly orderedSlots      = this._orderedSlots.asReadonly();
  readonly monIndicesByTeam  = this._monIndicesByTeam.asReadonly();
  readonly activeTeamIndex   = this._activeTeamIndex.asReadonly();
  // 16-bit liveness bitmap derived from orderedSlots; feeds the client-side slot allocator.
  readonly liveBitmap = computed<bigint>(() => {
    let bits = 0n;
    for (const slot of this._orderedSlots()) bits |= 1n << BigInt(slot);
    return bits;
  });
  readonly monIndicesForTeam = computed<readonly bigint[]>(
    () => this._monIndicesByTeam().get(this._activeTeamIndex()) ?? [],
  );
  readonly pointsBalance = this._pointsBalance.asReadonly();
  readonly playerData = this._playerData.asReadonly();
  readonly rollCost = this._rollCost.asReadonly();
  readonly openBattleOfferNonce = this._openBattleOfferNonce.asReadonly();
  readonly pairHashNonces = this._pairHashNonces.asReadonly();

  // The account exists (owns at least one mon) — not the same as being able to play.
  readonly hasMons = computed(() => {
    const indices = this._ownedMonIndices();
    if (indices === undefined) return undefined; // still loading
    return indices.length > 0;
  });

  // What every "can you play" gate actually needs: a live team slot. Owning mons
  // isn't enough — startBattle reverts without one.
  readonly hasTeam = computed(() => {
    if (this._ownedMonIndices() === undefined) return undefined; // still loading
    return this._orderedSlots().length > 0;
  });

  readonly matchmakerStatus = computed<MatchmakerStatus | undefined>(() => {
    if (!this._isLoaded()) return undefined;
    return {
      isCPUApproved: this._isCPUApproved(),
      isSignedMatchmakerApproved: this._isSignedMatchmakerApproved(),
    };
  });

  constructor() {
    this.publicClient = createPublicClient({
      chain: createAppChain(),
      transport: http(environment.rpcEndpoint, {
        fetchOptions: { cache: 'no-store' },
      }),
    });
  }

  async loadStateForAddress(address: Hex): Promise<void> {
    this._isLoading.set(true);
    this._isLoaded.set(false);
    this._activeAddress.set(address);

    const startTime = performance.now();
    if (isDevMode()) console.log(`[STARTUP ${performance.now().toFixed(2)}ms] Fetching onchain state...`);

    try {
      const multicallResults = await this.publicClient.multicall({
        contracts: [
          {
            address: environment.multicallAddress,
            abi: MulticallABI,
            functionName: 'getEthBalance',
            args: [address],
          },
          // Owned mons + per-mon exp/level/facet/move-selection in one read (no follow-up fan-out).
          {
            address: Address.GACHA_TEAM_REGISTRY,
            abi: GachaTeamRegistryABI,
            functionName: 'getOwnedMonDetails',
            args: [address],
          },
          // All live teams in display order + their mon ids in one batch view.
          {
            address: Address.GACHA_TEAM_REGISTRY,
            abi: GachaTeamRegistryABI,
            functionName: 'getPlayerTeams',
            args: [address],
          },
          {
            address: Address.ENGINE,
            abi: EngineABI,
            functionName: 'isMatchmakerFor',
            args: [address, Address.CPU],
          },
          {
            address: Address.ENGINE,
            abi: EngineABI,
            functionName: 'isMatchmakerFor',
            args: [address, Address.SIGNED_MATCHMAKER],
          },
          // Packed per-player profile: points (low 128b) + streak / quest-day / activity timestamps.
          {
            address: Address.GACHA_TEAM_REGISTRY,
            abi: GachaTeamRegistryABI,
            functionName: 'playerData',
            args: [address],
          },
          {
            address: Address.GACHA_TEAM_REGISTRY,
            abi: GachaTeamRegistryABI,
            functionName: 'ROLL_COST',
          },
        ],
      });

      if (isDevMode()) console.log(`[STARTUP ${performance.now().toFixed(2)}ms] OnchainStateService state fetched (${(performance.now() - startTime).toFixed(2)}ms)`);

      this._ethBalance.set(multicallResults[0].result as bigint ?? 0n);
      this.applyOwnedMonDetails(multicallResults[1].result as OwnedMonDetails | undefined);
      const teamsResult = multicallResults[2].result as PlayerTeamsResult | undefined;
      const slots = teamsResult ? teamsResult[0].map(Number) : [];
      const monIdsPerSlot = teamsResult ? teamsResult[1] : [];
      const teamMap = new Map<number, readonly bigint[]>();
      slots.forEach((slot, i) => teamMap.set(slot, monIdsPerSlot[i] ?? []));
      this._orderedSlots.set(slots);
      this._monIndicesByTeam.set(teamMap);
      // Default to first live slot; TeamListService reconciles against stored default.
      this._activeTeamIndex.set(slots[0] ?? 0);
      this._isCPUApproved.set(multicallResults[3].result as boolean ?? false);
      this._isSignedMatchmakerApproved.set(multicallResults[4].result as boolean ?? false);
      const playerData = (multicallResults[5].result as bigint) ?? 0n;
      this._playerData.set(playerData);
      this._pointsBalance.set(playerData & POINTS_MASK_128);
      const rollCost = multicallResults[6].result as bigint | undefined;
      if (rollCost) this._rollCost.set(rollCost);

      this._isLoaded.set(true);
    } finally {
      this._isLoading.set(false);
      this.resolveFirstLoad();
    }
  }

  // Decode the getOwnedMonDetails tuple into the owned-id set + the per-mon detail maps.
  // A reverted read (undefined) leaves ownedMonIndices unset so hasMons/hasTeam stay "loading".
  private applyOwnedMonDetails(details: OwnedMonDetails | undefined): void {
    if (!details) {
      this._ownedMonIndices.set(undefined);
      this._levelExpByMonId.set(new Map());
      this._facetByMonId.set(new Map());
      this._moveSelByMonId.set(new Map());
      return;
    }
    const [monIds, exp, levels, facetUnlocked, facetEquipped, moveSelections] = details;
    this._ownedMonIndices.set(monIds);

    const levelExp = new Map<string, { level: number; exp: number }>();
    const facets = new Map<string, { unlocked: number; equipped: number }>();
    const moveSel = new Map<string, number>();
    monIds.forEach((id, i) => {
      const key = id.toString();
      levelExp.set(key, { level: Number(levels[i] ?? 0n), exp: Number(exp[i] ?? 0n) });
      facets.set(key, { unlocked: Number(facetUnlocked[i] ?? 0), equipped: Number(facetEquipped[i] ?? 0) });
      moveSel.set(key, Number(moveSelections[i] ?? 0));
    });
    this._levelExpByMonId.set(levelExp);
    this._facetByMonId.set(facets);
    this._moveSelByMonId.set(moveSel);
  }

  getLevelExp(monId: bigint | number): { level: number; exp: number } | undefined {
    return this._levelExpByMonId().get(BigInt(monId).toString());
  }

  getFacetForMon(monId: bigint | number): { unlocked: number; equipped: number } | undefined {
    return this._facetByMonId().get(BigInt(monId).toString());
  }

  // Optimistic write — caller rolls back by re-calling with the prior `equipped`.
  setFacetForMon(monId: bigint, equipped: number): void {
    const key = monId.toString();
    const prev = this._facetByMonId().get(key);
    const next = new Map(this._facetByMonId());
    next.set(key, { unlocked: prev?.unlocked ?? 0, equipped });
    this._facetByMonId.set(next);
  }

  // 8-bit lane set, or undefined when not yet hydrated (caller falls back to the lanes-0..3 default).
  getMoveSelectionForMon(monId: bigint | number): number | undefined {
    return this._moveSelByMonId().get(BigInt(monId).toString());
  }

  // Optimistic write — caller rolls back by re-calling with the prior bitmap.
  setMoveSelectionForMon(monId: bigint, bitmap: number): void {
    const next = new Map(this._moveSelByMonId());
    next.set(monId.toString(), bitmap);
    this._moveSelByMonId.set(next);
  }

  setOwnedMonIndices(indices: readonly bigint[]): void {
    this._ownedMonIndices.set(indices);
  }

  setActiveTeamIndex(slot: number): void {
    this._activeTeamIndex.set(slot);
  }

  setMonIndicesForTeam(slot: number, indices: readonly bigint[]): void {
    const next = new Map(this._monIndicesByTeam());
    next.set(slot, indices);
    this._monIndicesByTeam.set(next);
  }

  appendCreatedTeam(slot: number, indices: readonly bigint[]): void {
    this.setMonIndicesForTeam(slot, indices);
    this._orderedSlots.set([...this._orderedSlots(), slot]);
  }

  removeTeam(slot: number): void {
    this._orderedSlots.set(this._orderedSlots().filter(s => s !== slot));
    const next = new Map(this._monIndicesByTeam());
    next.delete(slot);
    this._monIndicesByTeam.set(next);
  }

  setMatchmakerApproved(matchmaker: 'cpu' | 'signed', approved: boolean): void {
    const signals: Record<typeof matchmaker, WritableSignal<boolean>> = {
      cpu: this._isCPUApproved,
      signed: this._isSignedMatchmakerApproved,
    };
    signals[matchmaker].set(approved);
  }

  incrementOpenBattleOfferNonce(): void {
    this._openBattleOfferNonce.update(n => n + 1n);
  }

  async refreshOpenBattleOfferNonce(): Promise<bigint> {
    const address = this._activeAddress();
    if (!address) return this._openBattleOfferNonce();
    const nonce = await this.publicClient.readContract({
      address: Address.SIGNED_MATCHMAKER,
      abi: SignedMatchmakerABI,
      functionName: 'openBattleOfferNonce',
      args: [address],
    });
    this._openBattleOfferNonce.set(nonce);
    return nonce;
  }

  /**
   * Get pairHashNonce for a specific opponent (for directed challenges).
   * pairHash mirrors Engine.computeBattleKey: keccak256(abi.encode(lo, hi))
   * with the lower-valued address first, so we derive it locally and skip
   * the on-chain computeBattleKey read — one RPC down to one.
   */
  async getPairHashNonce(opponent: Hex): Promise<bigint> {
    const activeAddr = this._activeAddress();
    if (!activeAddr) {
      throw new Error('No active address');
    }

    const pairHash = computePairHash(activeAddr, opponent);

    const nonce = await this.publicClient.readContract({
      address: Address.ENGINE,
      abi: EngineABI,
      functionName: 'pairHashNonces',
      args: [pairHash],
    });

    this._pairHashNonces.update(map => {
      const newMap = new Map(map);
      newMap.set(pairHash, nonce);
      return newMap;
    });

    return nonce;
  }

  setPointsBalance(balance: bigint): void {
    this._pointsBalance.set(balance);
  }

  // Re-reads the full packed profile word (points + streak + quest-day + activity
  // timestamps) and re-syncs the derived points balance. Called after a battle settles.
  async refreshPlayerData(): Promise<bigint> {
    const address = this._activeAddress();
    if (!address) return this._playerData();
    const packed = await this.publicClient.readContract({
      address: Address.GACHA_TEAM_REGISTRY,
      abi: GachaTeamRegistryABI,
      functionName: 'playerData',
      args: [address],
    });
    // Wallet may have switched mid-flight; drop the stale result.
    if (this._activeAddress() !== address) return this._playerData();
    this._playerData.set(packed);
    this._pointsBalance.set(packed & POINTS_MASK_128);
    return packed;
  }

  addToPointsBalance(delta: bigint): void {
    this._pointsBalance.set(this._pointsBalance() + delta);
  }

  // Apply per-mon GachaEvent gains: bump exp by `expDelta` (recomputing level
  // via the mirrored on-chain curve) and OR `newFacetBitmap` into the unlocked
  // bitmap. Skips entirely when both deltas are zero. `newFacetBitmap` is the
  // 12-bit "facets drawn this battle" lane from the event — bit i set means
  // facet id (i+1) was drawn.
  applyMonGains(monId: bigint, expDelta: number, newFacetBitmap: number): void {
    const key = monId.toString();
    if (expDelta > 0) {
      const prev = this._levelExpByMonId().get(key) ?? { level: 0, exp: 0 };
      const newExp = prev.exp + expDelta;
      const next = new Map(this._levelExpByMonId());
      next.set(key, { level: levelForExp(newExp), exp: newExp });
      this._levelExpByMonId.set(next);
    }
    if (newFacetBitmap !== 0) {
      const prev = this._facetByMonId().get(key) ?? { unlocked: 0, equipped: 0 };
      const newUnlocked = prev.unlocked | (newFacetBitmap & FACET_UNLOCKED_MASK);
      const next = new Map(this._facetByMonId());
      next.set(key, { unlocked: newUnlocked, equipped: prev.equipped });
      this._facetByMonId.set(next);
    }
  }

  async fetchRollCost(): Promise<bigint> {
    if (this._rollCost() > 0n) {
      return this._rollCost();
    }

    // The state multicall carries ROLL_COST; the timeout covers a session that never loads state.
    await Promise.race([
      this.firstLoad,
      new Promise((resolve) => setTimeout(resolve, ROLL_COST_LOAD_WAIT_MS)),
    ]);
    if (this._rollCost() > 0n) {
      return this._rollCost();
    }

    const cost = await this.publicClient.readContract({
      address: Address.GACHA_TEAM_REGISTRY,
      abi: GachaTeamRegistryABI,
      functionName: 'ROLL_COST',
    });

    this._rollCost.set(cost);
    return cost;
  }

  reset(): void {
    this._isLoading.set(false);
    this._isLoaded.set(false);
    this._activeAddress.set(null);
    this._ethBalance.set(0n);
    this._ownedMonIndices.set(undefined);
    this._orderedSlots.set([]);
    this._monIndicesByTeam.set(new Map());
    this._activeTeamIndex.set(0);
    this._isCPUApproved.set(false);
    this._isSignedMatchmakerApproved.set(false);
    this._pointsBalance.set(0n);
    this._playerData.set(0n);
    this._openBattleOfferNonce.set(0n);
    this._pairHashNonces.set(new Map());
  }
}
